import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Flame, Zap, Sparkles, Crown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import confetti from 'canvas-confetti';

const ACHIEVEMENT_ICONS = {
  trophy: Trophy,
  star: Star,
  flame: Flame,
  zap: Zap,
  sparkles: Sparkles,
  crown: Crown
};

const RARITY_STYLES = {
  common: {
    gradient: 'from-stone-200 to-stone-300',
    border: 'border-stone-400',
    glow: 'shadow-stone-300/50',
    text: 'text-stone-700'
  },
  rare: {
    gradient: 'from-blue-400 to-blue-600',
    border: 'border-blue-400',
    glow: 'shadow-blue-400/50',
    text: 'text-blue-100'
  },
  epic: {
    gradient: 'from-purple-500 to-purple-700',
    border: 'border-purple-400',
    glow: 'shadow-purple-400/50',
    text: 'text-purple-100'
  },
  legendary: {
    gradient: 'from-amber-400 via-yellow-500 to-orange-500',
    border: 'border-amber-400',
    glow: 'shadow-amber-400/50',
    text: 'text-amber-100'
  }
};

const RARITY_LABELS = {
  de: { common: 'Gewöhnlich', rare: 'Selten', epic: 'Episch', legendary: 'Legendär' },
  en: { common: 'Common', rare: 'Rare', epic: 'Epic', legendary: 'Legendary' }
};

export default function AchievementPopup({ 
  achievement, 
  isOpen, 
  onClose,
  language = 'de' 
}) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen && achievement) {
      setIsVisible(true);
      
      // Trigger confetti for higher rarities
      if (achievement.rarity === 'epic' || achievement.rarity === 'legendary') {
        const colors = achievement.rarity === 'legendary' 
          ? ['#fbbf24', '#f59e0b', '#d97706']
          : ['#a855f7', '#9333ea', '#7e22ce'];
        
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: colors
        });
      }
    }
  }, [isOpen, achievement]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  if (!achievement) return null;

  const rarity = achievement.rarity || 'common';
  const styles = RARITY_STYLES[rarity];
  const IconComponent = ACHIEVEMENT_ICONS[achievement.icon] || Trophy;
  const rarityLabel = RARITY_LABELS[language]?.[rarity] || rarity;

  const title = language === 'de' 
    ? (achievement.name_de || achievement.name)
    : (achievement.name_en || achievement.name || achievement.name_de);
  
  const description = language === 'de'
    ? (achievement.description_de || achievement.description)
    : (achievement.description_en || achievement.description || achievement.description_de);

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
          />
          
          {/* Popup */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.5, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 15, stiffness: 300 }}
            className={`
              relative w-[90%] max-w-sm mx-auto p-1 rounded-2xl
              bg-gradient-to-br ${styles.gradient}
              shadow-2xl ${styles.glow}
            `}
          >
            {/* Inner card */}
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 relative overflow-hidden">
              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>

              {/* Achievement unlocked header */}
              <motion.div
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-center mb-4"
              >
                <span className="text-xs font-semibold uppercase tracking-wider text-[#8a6d10]">
                  {language === 'de' ? '🎉 Achievement freigeschaltet!' : '🎉 Achievement Unlocked!'}
                </span>
              </motion.div>

              {/* Icon */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", delay: 0.2, damping: 10 }}
                className="flex justify-center mb-4"
              >
                <div className={`
                  w-20 h-20 rounded-full flex items-center justify-center
                  bg-gradient-to-br ${styles.gradient}
                  border-4 ${styles.border}
                  shadow-lg
                `}>
                  <IconComponent className={`h-10 w-10 ${rarity === 'common' ? 'text-stone-600' : 'text-white'}`} />
                </div>
              </motion.div>

              {/* Title & Description */}
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-center space-y-2"
              >
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {description}
                </p>
              </motion.div>

              {/* Rarity badge */}
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="flex justify-center mt-4"
              >
                <span className={`
                  px-3 py-1 rounded-full text-xs font-semibold uppercase
                  bg-gradient-to-r ${styles.gradient} ${rarity === 'common' ? 'text-stone-700' : 'text-white'}
                `}>
                  {rarityLabel}
                </span>
              </motion.div>

              {/* Points reward */}
              {achievement.points_reward && (
                <motion.div
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="flex justify-center items-center gap-2 mt-4 text-[#8a6d10] font-semibold"
                >
                  <Sparkles className="h-4 w-4" />
                  <span>+{achievement.points_reward} {language === 'de' ? 'Punkte' : 'Points'}</span>
                </motion.div>
              )}

              {/* Continue button */}
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-6"
              >
                <Button 
                  onClick={handleClose}
                  className="w-full bg-[#C9A23C] hover:bg-[#E0BE5A] text-[#1a1a1a]"
                >
                  {language === 'de' ? 'Weiter' : 'Continue'}
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
